import {
    Button,
    Notifications,
    Progress,
    ViewLabelStatus,
} from "d-react-components";
import { useFormik } from "formik";
import { useContext, useMemo, useState } from "react";
import * as Yup from "yup";
import StockRequestAPI from "../../../../api/queries/stock-request";
import ActionMenu from "../../../../common/ActionButton";
import Drawer from "../../../../common/Drawer";
import InputTextForm from "../../../../common/input/InputTextForm";
import {
    STOCK_REQUEST_STATUS,
    STOCK_REQUEST_STATUSES,
} from "../../../../constant/stock-request";
import { StockRequestDetailContext } from "../../../../context/stock-request";
import Messages from "../../../../languages/Messages";

interface IStockRequestConfirmDrawer {
    open: boolean;
    title?: string;
    description?: string;
    onClose: () => void;
    onConfirm: (props: any) => void;
}

const StockRequestConfirmDrawer = ({
    open,
    onClose,
    onConfirm,
    title,
    description,
}: IStockRequestConfirmDrawer) => {
    const confirmForm = useFormik<any>({
        initialValues: {} as any,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: Yup.object().shape({
            remark: Yup.string().required("Is Required!"),
        }),
        onSubmit: (values: any) => {
            onClose && onClose();
            onConfirm(values);
        },
    });

    const formValues = confirmForm?.values;
    const formErrors = confirmForm?.errors;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            title={title}
            onSave={() => confirmForm.handleSubmit()}
            size="auto"
            width="40%"
        >
            {description && <p className="text px-4 mt-4">{description}</p>}
            <div className="px-4">
                <InputTextForm form={confirmForm} keyData="remark" multiple />
            </div>
        </Drawer>
    );
};

const StockRequestHeader = () => {
    const { stockRequest, setStockRequest } = useContext(
        StockRequestDetailContext
    );
    const [openConfirm, setOpenConfirm] = useState<{
        open: boolean;
        type?: "approve" | "cancel";
    }>({ open: false });

    const isShowConfirm = useMemo(() => {
        return stockRequest?.status === STOCK_REQUEST_STATUS.PENDING;
    }, [stockRequest?.status]);

    const onClickAction = (item: any) => {
        if (item.id === "cancelRequest") {
            setOpenConfirm({ open: true, type: "cancel" });
        }
    };

    const onApproveRequest = (input: any) => {
        Progress.show(
            {
                method: StockRequestAPI.approve,
                params: [
                    { id: stockRequest?.id, remark: input?.remark ?? null },
                ],
            },
            (res: any) => {
                if (res) {
                    Notifications.showSuccess(Messages.cancelSuccess);
                    setStockRequest(res);
                }
            }
        );
    };

    const onCancelRequest = (input: any) => {
        Progress.show(
            {
                method: StockRequestAPI.cancel,
                params: [
                    { id: stockRequest?.id, remark: input?.remark ?? null },
                ],
            },
            (res: any) => {
                if (res) {
                    Notifications.showSuccess(Messages.cancelSuccess);
                    setStockRequest(res);
                }
            }
        );
    };

    return (
        <div className="bg-white p-3 flex-row-between-center border-bottom">
            <div className="d-flex">
                <h5 className="text-primary">{`${stockRequest?.requestNo}`}</h5>
                <ViewLabelStatus
                    status={stockRequest?.status}
                    listStatus={STOCK_REQUEST_STATUSES}
                    getLabel={(item) => (Messages as any)[item.label]}
                    className="ml-3"
                />
            </div>
            <div className="d-flex">
                <Button
                    variant="outline"
                    size="small"
                    onClick={() =>
                        setOpenConfirm({ open: true, type: "approve" })
                    }
                    className="ml-2"
                    hidden={!isShowConfirm}
                >
                    {Messages.approve}
                </Button>
                <Button
                    variant="outline"
                    size="small"
                    onClick={() => {}}
                    className="ml-2"
                >
                    {Messages.print}
                </Button>
                <ActionMenu
                    className="ml-2"
                    dataSource={[
                        {
                            id: "cancelRequest",
                            label: Messages.cancelRequest,
                        },
                    ]}
                    onClick={onClickAction}
                />
            </div>
            {openConfirm?.open && (
                <StockRequestConfirmDrawer
                    title={
                        openConfirm?.type === "approve"
                            ? Messages.approvalConfirmation
                            : Messages.cancelConfirmation
                    }
                    description={
                        openConfirm?.type === "approve"
                            ? Messages.approvalRequestedConfirmationDes
                            : Messages.cancelRequestedConfirmationDes
                    }
                    open={openConfirm?.open}
                    onClose={() => setOpenConfirm({ open: false })}
                    onConfirm={(input) =>
                        openConfirm?.type === "approve"
                            ? onApproveRequest(input)
                            : onCancelRequest(input)
                    }
                />
            )}
        </div>
    );
};

export default StockRequestHeader;
